import {Injectable, Injector} from "@angular/core";
import {ReportError} from "app/app.service";

import {BaseListService} from "app/services/domain/base.service";
import {ProjectFlat} from "app/appsecops/app-onboarding/app-onboarding-page/project/list-project/list-project.component";
import * as Models from "app/services/proxy/model/models";

import {Lookup} from "framework/services/utils.service";
import {Memoizer} from "framework/utils/memoizers";

@Injectable()
export class AppOnboardingProjectService extends BaseListService<Models.AppSecopsProjectListRequest, Models.BatchRequest, Models.AppSecopsProject>
{
    private readonly m_projectsByTenant: Lookup<Models.AppSecopsProject[]>          = {};
    private readonly m_fetchingByTenant: Lookup<Promise<Models.AppSecopsProject[]>> = {};

    constructor(inj: Injector)
    {
        super(inj, Models.AppSecopsProject);
    }

    @ReportError
    public list(listReq: Models.AppSecopsProjectListRequest): Promise<Models.ListResponse>
    {
        return this.domain.apis.appSecOps.listProject(listReq);
    }

    protected async getBatchResponseImpl(req: Models.BatchRequest): Promise<Models.AppSecopsProjectBatchResponse>
    {
        const recordIdBatchRequest = Models.BatchRequest.newInstance(req);

        return this.domain.apis.appSecOps.batchProject(recordIdBatchRequest);
    }

    protected createBatchRequest(listReq: Models.AppSecopsProjectListRequest,
                                 ids: string[]): Models.BatchRequest
    {
        return Models.BatchRequest.newInstance({ids: ids});
    }

    public async getAllForTenant(tenantId: string): Promise<Models.AppSecopsProject[]>
    {
        if (this.m_projectsByTenant[tenantId])
        {
            return this.m_projectsByTenant[tenantId];
        }

        if (!this.m_fetchingByTenant[tenantId])
        {
            this.m_fetchingByTenant[tenantId] = (async () =>
            {
                try
                {
                    const request = Models.AppSecopsProjectListRequest.newInstance({
                                                                                       filters: Models.AppSecopsProjectFilterBase.newInstance({
                                                                                                                                                  tenant_id: tenantId
                                                                                                                                              })
                                                                                   });
                    const ids     = await this.listAll(request);
                    if (ids.length)
                    {
                        this.m_projectsByTenant[tenantId] = await this.batchAll(ids);
                    }
                }
                finally
                {
                    delete this.m_fetchingByTenant[tenantId];
                }

                return this.m_projectsByTenant[tenantId];
            })();
        }

        return this.m_fetchingByTenant[tenantId];
    }

    public resetTenantCache(tenantId: string)
    {
        this.m_projectsByTenant[tenantId] = null;
    }

    public async getAllProjectNamesForTenant(tenantId: string): Promise<string[]>
    {
        const allProjects = await this.getAllForTenant(tenantId);
        return allProjects?.map(project => project.name);
    }

    public async isAtleastOneProjectExists(tenantId: string): Promise<boolean>
    {
        let project: Models.ListResponse;
        let context   = Models.ListContext.newInstance({
                                                           batch_offset: 0,
                                                           batch_size  : 1,
                                                           total       : 0
                                                       });
        const request = Models.AppSecopsProjectListRequest.newInstance({
                                                                           list_context: context,
                                                                           filters     : Models.AppSecopsProjectFilterBase.newInstance({
                                                                                                                                           tenant_id: tenantId
                                                                                                                                       })
                                                                       });
        try
        {
            project = await this.app.domain.appOnboardingProject.list(request);
        }
        catch
        {
            return false;
        }
        finally
        {
            return project && project.results.length > 0;
        }
    }

    public getProjectById(portfolioHierarchy: Models.AppSecopsPortfolioHierarchyResponse[],
                          projectId: string): Models.AppSecopsProject
    {
        let project: Models.AppSecopsProject = null;
        for (const portfolio of portfolioHierarchy)
        {
            if (portfolio.applications)
            {
                for (const application of portfolio.applications)
                {
                    if (application.projects)
                    {
                        project = application.projects.find(proj => proj.project_id === projectId);
                        if (project)
                        {
                            return project;
                        }
                    }
                }
            }
        }
        return null;
    }

    public async loadSbomForProjects(projects: ProjectFlat[]): Promise<void>
        {
            if (!projects) return;
    
            const projectIds = projects.map((project: ProjectFlat) => project.model.project_id);
            try
            {
                const listResponse = await this.app.domain.apis.appSecOps.listSbomDefinitions(Models.AppSecopsSbomDefinitionsListRequest.newInstance({
                                                                                                                                                        filters: Models.AppSecopsSbomDefinitionsFilter.newInstance({
                                                                                                                                                                                                                        tenant_id: this.app.domain.users.lastActiveTenantId,
                                                                                                                                                                                                                        filters  : Models.AppSecopsProjectIdQueryOperator.newInstance(
                                                                                                                                                                                                                            {
                                                                                                                                                                                                                                appsecops_project_ids: projectIds
                                                                                                                                                                                                                            })
                                                                                                                                                                                                                    })
                                                                                                                                                    })
                );
    
                if (!listResponse?.results?.length) return;
    
                const batchResponse = await this.app.domain.apis.appSecOps.batchSbomDefinitions(Models.BatchRequest.newInstance({ids: listResponse.results}));
    
                if (batchResponse)
                {
                    projects.forEach((project: ProjectFlat) =>
                                     {
                                         project.sbom = batchResponse
                                             .filter((sbom: Models.AppSecopsSbomDefinition) => sbom.appsecops_project_id === project.model.project_id)
                                             .map((sbom: Models.AppSecopsSbomDefinition) => sbom.name);
                                     });
                }
            }
            finally
            {
                projects.forEach((project: ProjectFlat) => project.sbomPromise?.resolve());
            }
        }

    @Memoizer
    public async sbomMappedProjects(tenantId: string): Promise<Models.ListResponse>
    {
        const filters = Models.AppSecopsProjectFilterBase.newInstance({
                                                                          tenant_id  : tenantId,
                                                                          sbom_filter: Models.AppSecopsProjectSbomFilter.with_sbom
                                                                      });
        return this.app.domain.appOnboardingProject.list(Models.AppSecopsProjectListRequest.newInstance({
                                                                                                            filters,
                                                                                                            sort: [
                                                                                                                Models.AppSecopsProjectSortCriteria.newInstance({
                                                                                                                                                                    column   : Models.AppSecopsProjectSortColumn.updated_at,
                                                                                                                                                                    ascending: false
                                                                                                                                                                })
                                                                                                            ]
                                                                                                        }));
    }
}
